import { ReactElement } from "react";
import { Link } from "react-router-dom";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { describeSummary } from "../../rowText";
import { AttentionSurface, trackAttentionOpenList } from "../../tracking";
import useAttention from "../../useAttention";
import "./AttentionBand.scss";

interface IProps {
    /**
     * Called when the band is followed, so the menu holding it can close itself.
     */
    onNavigate?: () => void;
}

/**
 * One line at the top of the responsive menu: how much is waiting and what the
 * most urgent of it is.
 *
 * Never a list. The menu is a place to go somewhere from, and a second list of
 * rows inside it would be the same objects shown twice in one screen.
 */
function AttentionBand(props: IProps): ReactElement | null {
    const { list } = useAttention();

    if (!list || !list.applicable) {
        return null;
    }

    const isOverdue = list.rows.some((row) => row.isOverdue);
    const isAllClear = list.count === 0;

    return (
        <Link
            className={isAllClear ? "AttentionBand AttentionBand--clear" : "AttentionBand"}
            to="/notifications"
            onClick={() => {
                trackAttentionOpenList(AttentionSurface.MenuBand, list);
                props.onNavigate?.();
            }}
        >
            <span className="AttentionBand-main">
                {isAllClear ? (
                    <span className="AttentionBand-tick" aria-hidden="true">
                        <Visage2Icon icon="tick-circle" size="extra-small" />
                    </span>
                ) : (
                    <span
                        className={isOverdue
                            ? "AttentionBand-counter AttentionBand-counter--late"
                            : "AttentionBand-counter"}
                    >
                        {list.count}
                    </span>
                )}

                <span className="AttentionBand-text">
                    {/* "Inget väntar", not the "Allt klart" in W1b. The menu header
                        already shows "Allt klart" a few rows up for system status,
                        and the same phrase twice on one screen meaning two different
                        things is the confusion this surface exists to avoid. */}
                    <span className="AttentionBand-title">
                        {isAllClear ? localize("ATTENTION_ALL_CLEAR_TITLE") : localize("ATTENTION_TITLE")}
                    </span>
                    <span className="AttentionBand-summary">
                        {isAllClear
                            ? localize("ATTENTION_ALL_CLEAR")
                            : describeSummary(list.rows, list.count)}
                    </span>
                </span>
            </span>

            <Visage2Icon icon="arrow-right-3" size="extra-small" />
        </Link>
    );
}

export default AttentionBand;
